import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { firebaseAuth, firestoreDb } from '../services/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import type {
  AppTheme,
  CanvasBackgroundKey,
  ClientUserPreferences,
  DigestCadence,
  FocusSoundscapeKey,
  QuietHoursWindow,
} from '../types/schema'
import {
  ClientPreferencesContext,
  DEFAULT_CLIENT_USER_PREFERENCES,
  type ClientPreferencesContextValue,
} from './clientPreferencesContextCore'
import {
  getSurfaceThemeOverride,
  subscribeSurfaceThemeOverride,
} from '../services/surfaceThemeOverride'
import {
  applyMosaicMove,
  isLightTheme,
  mapToMosaicTheme,
} from '../utils/mosaicMove'
import { THEME_CATALOG } from '../utils/themeCatalog'
import {
  effectiveCanvasBackground,
  effectiveFocusSoundscape,
} from '../utils/workspaceModulesCosmetics'

const STORAGE_KEY = 'timelining-client-user-preferences'
const LEGACY_MEMBER_THEME_STORAGE_KEY = 'timelining-member-app-theme'
const MEMBER_THEME_FIRESTORE_FIELD = 'memberAppTheme'

const VALID_THEMES: AppTheme[] = [
  'MOSAIC_LIGHT',
  'MOSAIC_DARK',
  'LIGHT',
  'DARK',
  'OLED_BLACK',
  'MIDNIGHT',
  'AURORA',
  'EMBER',
  'CELESTIAL',
  'PEARL',
  'LINEN',
  'GLACIER',
  'BLUSH',
  'SAGE',
]
const VALID_BACKGROUNDS: CanvasBackgroundKey[] = ['DEFAULT', 'BLUEPRINT_GRID', 'NEBULA', 'CIRCUIT']
const VALID_SOUNDSCAPES: FocusSoundscapeKey[] = ['SILENCE', 'LOFI_RAIN', 'ALPHA_WAVE']

const isTheme = (value: unknown): value is AppTheme =>
  typeof value === 'string' && (VALID_THEMES as string[]).includes(value)

const isThemeMode = (value: unknown): value is 'FIXED' | 'SYSTEM' =>
  value === 'FIXED' || value === 'SYSTEM'

const readLegacyMemberTheme = (): AppTheme | null => {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.localStorage.getItem(LEGACY_MEMBER_THEME_STORAGE_KEY)
    return isTheme(stored) ? stored : null
  } catch {
    return null
  }
}

const clearLegacyMemberTheme = () => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(LEGACY_MEMBER_THEME_STORAGE_KEY)
  } catch {
    // Best-effort cleanup for the retired member theme hook.
  }
}

const isAnimationProfile = (value: unknown): value is ClientUserPreferences['animationProfile'] =>
  value === 'FLUID' || value === 'FAST' || value === 'REDUCED_MOTION'

const isStatusMarkers = (value: unknown): value is ClientUserPreferences['statusMarkers'] =>
  value === 'SHORT' || value === 'LONG'

const isLoadingStyle = (value: unknown): value is ClientUserPreferences['loadingStyle'] =>
  value === 'CONSTELLATION' || value === 'STARFIELD'

const isCanvasBackground = (value: unknown): value is CanvasBackgroundKey =>
  typeof value === 'string' && (VALID_BACKGROUNDS as string[]).includes(value)

const isFocusSoundscape = (value: unknown): value is FocusSoundscapeKey =>
  typeof value === 'string' && (VALID_SOUNDSCAPES as string[]).includes(value)

const VALID_DIGEST_CADENCES: DigestCadence[] = ['DAILY', 'WEEKLY']

const isDigestCadence = (value: unknown): value is DigestCadence =>
  typeof value === 'string' && (VALID_DIGEST_CADENCES as string[]).includes(value)

const isValidHour = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 23

const normalizeQuietHours = (value: unknown): QuietHoursWindow | null => {
  if (!value || typeof value !== 'object') return null
  const raw = value as Partial<QuietHoursWindow>
  if (!isValidHour(raw.startHour) || !isValidHour(raw.endHour)) return null
  if (typeof raw.timezone !== 'string' || raw.timezone.trim() === '') return null
  if (raw.startHour === raw.endHour) return null
  return { startHour: raw.startHour, endHour: raw.endHour, timezone: raw.timezone.trim() }
}

const normalizeIsoString = (value: unknown) => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  const parsed = Date.parse(trimmed)
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : ''
}

const clampNumber = (value: unknown, fallback: number, min: number, max: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(max, Math.max(min, value))
}

// New users default to Mosaic Dark or Mosaic Light based on OS color-scheme preference
// (prefers-color-scheme: dark). Once they save any preference, that stored value wins.
const getOsPreferredTheme = (): AppTheme => {
  if (typeof window === 'undefined') return 'MOSAIC_LIGHT'
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'MOSAIC_DARK' : 'MOSAIC_LIGHT'
}

const normalizeLookVersion = (value: unknown): number | undefined => {
  if (value === undefined) return undefined
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return 0
  return Math.floor(value)
}

const normalizePreferences = (value: unknown): ClientUserPreferences => {
  const data = value && typeof value === 'object'
    ? value as Partial<ClientUserPreferences>
    : {}

  return {
    theme: isTheme(data.theme) ? data.theme : getOsPreferredTheme(),
    themeMode: isThemeMode(data.themeMode) ? data.themeMode : undefined,
    memberAppTheme: data.memberAppTheme === null
      ? null
      : isTheme(data.memberAppTheme)
        ? data.memberAppTheme
        : DEFAULT_CLIENT_USER_PREFERENCES.memberAppTheme,
    animationProfile: isAnimationProfile(data.animationProfile)
      ? data.animationProfile
      : DEFAULT_CLIENT_USER_PREFERENCES.animationProfile,
    statusMarkers: isStatusMarkers(data.statusMarkers)
      ? data.statusMarkers
      : DEFAULT_CLIENT_USER_PREFERENCES.statusMarkers,
    loadingStyle: isLoadingStyle(data.loadingStyle)
      ? data.loadingStyle
      : DEFAULT_CLIENT_USER_PREFERENCES.loadingStyle,
    canvasBackground: isCanvasBackground(data.canvasBackground)
      ? data.canvasBackground
      : DEFAULT_CLIENT_USER_PREFERENCES.canvasBackground,
    focusSoundscape: isFocusSoundscape(data.focusSoundscape)
      ? data.focusSoundscape
      : DEFAULT_CLIENT_USER_PREFERENCES.focusSoundscape,
    soundEnabled: typeof data.soundEnabled === 'boolean'
      ? data.soundEnabled
      : DEFAULT_CLIENT_USER_PREFERENCES.soundEnabled,
    hapticEnabled: typeof data.hapticEnabled === 'boolean'
      ? data.hapticEnabled
      : DEFAULT_CLIENT_USER_PREFERENCES.hapticEnabled,
    highFidelityEffectsEnabled: typeof data.highFidelityEffectsEnabled === 'boolean'
      ? data.highFidelityEffectsEnabled
      : DEFAULT_CLIENT_USER_PREFERENCES.highFidelityEffectsEnabled,
    watchdogLeaseTimeoutMinutes: clampNumber(
      data.watchdogLeaseTimeoutMinutes,
      DEFAULT_CLIENT_USER_PREFERENCES.watchdogLeaseTimeoutMinutes,
      1,
      60,
    ),
    railCollapsedShowLabels: typeof data.railCollapsedShowLabels === 'boolean'
      ? data.railCollapsedShowLabels
      : DEFAULT_CLIENT_USER_PREFERENCES.railCollapsedShowLabels,
    railAutoPopupOnHover: typeof data.railAutoPopupOnHover === 'boolean'
      ? data.railAutoPopupOnHover
      : DEFAULT_CLIENT_USER_PREFERENCES.railAutoPopupOnHover,
    aiMagicRequireQuickAnswerSubmit: typeof data.aiMagicRequireQuickAnswerSubmit === 'boolean'
      ? data.aiMagicRequireQuickAnswerSubmit
      : DEFAULT_CLIENT_USER_PREFERENCES.aiMagicRequireQuickAnswerSubmit,
    aiMagicDraftReadyNotifications: typeof data.aiMagicDraftReadyNotifications === 'boolean'
      ? data.aiMagicDraftReadyNotifications
      : DEFAULT_CLIENT_USER_PREFERENCES.aiMagicDraftReadyNotifications,
    aiMagicAutoOpenManageDrafts: typeof data.aiMagicAutoOpenManageDrafts === 'boolean'
      ? data.aiMagicAutoOpenManageDrafts
      : DEFAULT_CLIENT_USER_PREFERENCES.aiMagicAutoOpenManageDrafts,
    enableCelebration: typeof data.enableCelebration === 'boolean'
      ? data.enableCelebration
      : DEFAULT_CLIENT_USER_PREFERENCES.enableCelebration,
    enableDebugMode: typeof data.enableDebugMode === 'boolean'
      ? data.enableDebugMode
      : DEFAULT_CLIENT_USER_PREFERENCES.enableDebugMode,
    supportTicketLastSeenAt: normalizeIsoString(data.supportTicketLastSeenAt),
    collapsingHeaderEnabled: typeof data.collapsingHeaderEnabled === 'boolean'
      ? data.collapsingHeaderEnabled
      : DEFAULT_CLIENT_USER_PREFERENCES.collapsingHeaderEnabled,
    digestCadence: isDigestCadence(data.digestCadence)
      ? data.digestCadence
      : DEFAULT_CLIENT_USER_PREFERENCES.digestCadence,
    quietHours: data.quietHours === null
      ? null
      : data.quietHours === undefined
        ? DEFAULT_CLIENT_USER_PREFERENCES.quietHours
        : normalizeQuietHours(data.quietHours),
    lookVersion: normalizeLookVersion(data.lookVersion),
    mosaicToastPending: typeof data.mosaicToastPending === 'boolean'
      ? data.mosaicToastPending
      : undefined,
  }
}

const loadPreferences = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      const preferences = normalizePreferences(parsed)
      const data = parsed && typeof parsed === 'object'
        ? parsed as Partial<ClientUserPreferences>
        : {}
      const legacyTheme = data.memberAppTheme === undefined ? readLegacyMemberTheme() : null
      clearLegacyMemberTheme()
      const base = legacyTheme ? { ...preferences, memberAppTheme: legacyTheme } : preferences
      return applyMosaicMove(base)
    }
    const legacyTheme = readLegacyMemberTheme()
    clearLegacyMemberTheme()
    const defaults: ClientUserPreferences = {
      ...normalizePreferences(null),
      lookVersion: 1,
      mosaicToastPending: false,
    }
    return legacyTheme ? { ...defaults, memberAppTheme: legacyTheme } : defaults
  } catch {
    const legacyTheme = readLegacyMemberTheme()
    clearLegacyMemberTheme()
    const defaults: ClientUserPreferences = {
      ...normalizePreferences(null),
      lookVersion: 1,
      mosaicToastPending: false,
    }
    return legacyTheme
      ? { ...defaults, memberAppTheme: legacyTheme }
      : defaults
  }
}

const THEME_ATTRIBUTE: Record<AppTheme, string> = {
  MOSAIC_LIGHT: 'mosaic-light',
  MOSAIC_DARK: 'mosaic-dark',
  LIGHT: 'light',
  DARK: 'dark',
  OLED_BLACK: 'oled-black',
  MIDNIGHT: 'midnight',
  AURORA: 'aurora',
  EMBER: 'ember',
  CELESTIAL: 'celestial',
  PEARL: 'pearl',
  LINEN: 'linen',
  GLACIER: 'glacier',
  BLUSH: 'blush',
  SAGE: 'sage',
}

const getThemeModeAttribute = (theme: AppTheme): 'light' | 'dark' =>
  isLightTheme(theme) ? 'light' : 'dark'

const CANVAS_BG_ATTRIBUTE: Record<CanvasBackgroundKey, string> = {
  DEFAULT: 'default',
  BLUEPRINT_GRID: 'blueprint-grid',
  NEBULA: 'nebula',
  CIRCUIT: 'circuit',
}

const FOCUS_SOUNDSCAPE_ATTRIBUTE: Record<FocusSoundscapeKey, string> = {
  SILENCE: 'silence',
  LOFI_RAIN: 'lofi-rain',
  ALPHA_WAVE: 'alpha-wave',
}

const getThemeAttribute = (theme: AppTheme) => THEME_ATTRIBUTE[theme] ?? 'dark'

const getMotionAttribute = (profile: ClientUserPreferences['animationProfile']) => {
  if (profile === 'REDUCED_MOTION') return 'reduced'
  if (profile === 'FAST') return 'fast'
  return 'fluid'
}

const getStatusMarkersAttribute = (marker: ClientUserPreferences['statusMarkers']) =>
  marker === 'LONG' ? 'long' : 'short'

const getLoadingStyleAttribute = (style: ClientUserPreferences['loadingStyle']) =>
  style === 'STARFIELD' ? 'starfield' : 'constellation'

const getIsMobileViewport = () =>
  typeof window !== 'undefined'
    ? window.matchMedia('(max-width: 767px)').matches
    : false

export const ClientPreferencesProvider = ({ children }: PropsWithChildren) => {
  const [preferences, setPreferences] = useState<ClientUserPreferences>(() => loadPreferences())
  const [isMobileViewport, setIsMobileViewport] = useState(() => getIsMobileViewport())
  const [accountLookMove, setAccountLookMove] = useState<'unknown' | 'pending' | 'done'>('unknown')
  const preferencesRef = useRef(preferences)
  useEffect(() => {
    preferencesRef.current = preferences
  }, [preferences])
  const lastSyncedRailPreferences = useRef<Pick<ClientUserPreferences, 'railCollapsedShowLabels' | 'railAutoPopupOnHover'> | null>(null)
  const lastSyncedDigestPreferences = useRef<Pick<ClientUserPreferences, 'digestCadence' | 'quietHours'> | null>(null)
  const lastSyncedMemberTheme = useRef<AppTheme | null | undefined>(undefined)
  const mappedMemberThemeRef = useRef<AppTheme | null | undefined>(undefined)
  const lastSyncedSupportLastSeenAt = useRef<string | null>(null)
  const currentUidRef = useRef<string | null>(null)

  const [surfaceThemeOverride, setSurfaceThemeOverrideState] = useState<AppTheme | null>(() => getSurfaceThemeOverride())
  // funPackThemeFallback covers cosmetic fallbacks: unlockable themes, canvas backgrounds, and focus soundscapes.
  const [funPackThemeFallback, setFunPackThemeFallback] = useState(false)

  useEffect(() => subscribeSurfaceThemeOverride(setSurfaceThemeOverrideState), [])

  const [osTheme, setOsTheme] = useState<AppTheme>(() => getOsPreferredTheme())

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const syncTheme = (e?: MediaQueryListEvent | { matches?: boolean }) => {
      const isDark = typeof e?.matches === 'boolean' ? e.matches : mediaQuery.matches
      setOsTheme(isDark ? 'MOSAIC_DARK' : 'MOSAIC_LIGHT')
    }
    syncTheme()
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncTheme)
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(syncTheme)
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', syncTheme)
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(syncTheme)
      }
    }
  }, [])

  useEffect(() => {
    const isSystem = preferences.themeMode === 'SYSTEM'
    const chosen = surfaceThemeOverride ?? (isSystem ? osTheme : preferences.theme)
    const isUnlockable = THEME_CATALOG.find((entry) => entry.key === chosen)?.cosmeticId !== undefined
    const effectiveTheme = (funPackThemeFallback && isUnlockable)
      ? (getThemeModeAttribute(chosen) === 'dark' ? 'MOSAIC_DARK' : 'MOSAIC_LIGHT')
      : chosen
    const effectiveBg = effectiveCanvasBackground(preferences.canvasBackground, funPackThemeFallback)
    const effectiveSoundscape = effectiveFocusSoundscape(preferences.focusSoundscape, funPackThemeFallback)
    document.documentElement.setAttribute('data-theme', getThemeAttribute(effectiveTheme))
    document.documentElement.setAttribute('data-theme-mode', getThemeModeAttribute(effectiveTheme))
    document.documentElement.setAttribute('data-animation-profile', getMotionAttribute(preferences.animationProfile))
    document.documentElement.setAttribute('data-status-markers', getStatusMarkersAttribute(preferences.statusMarkers))
    document.documentElement.setAttribute('data-loading-style', getLoadingStyleAttribute(preferences.loadingStyle))
    document.documentElement.setAttribute('data-canvas-bg', CANVAS_BG_ATTRIBUTE[effectiveBg])
    document.documentElement.setAttribute('data-focus-soundscape', FOCUS_SOUNDSCAPE_ATTRIBUTE[effectiveSoundscape])
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
  }, [preferences, surfaceThemeOverride, funPackThemeFallback, osTheme])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      if (!user) {
        currentUidRef.current = null
        lastSyncedRailPreferences.current = null
        lastSyncedDigestPreferences.current = null
        lastSyncedMemberTheme.current = undefined
        lastSyncedSupportLastSeenAt.current = null
        mappedMemberThemeRef.current = undefined
        setAccountLookMove('unknown')
        return
      }
      currentUidRef.current = user.uid
      try {
        const snapshot = await getDoc(doc(firestoreDb, 'users', user.uid))
        const data = snapshot.data()
        const rawAccountLookVersion = data?.lookVersion
        const accountLookVersion = typeof rawAccountLookVersion === 'number' && Number.isFinite(rawAccountLookVersion)
          ? rawAccountLookVersion
          : 0
        setAccountLookMove(accountLookVersion < 1 ? 'pending' : 'done')
        const storedRailPreferences = {
          railCollapsedShowLabels: typeof data?.railCollapsedShowLabels === 'boolean'
            ? data.railCollapsedShowLabels
            : null,
          railAutoPopupOnHover: typeof data?.railAutoPopupOnHover === 'boolean'
            ? data.railAutoPopupOnHover
            : null,
        }
        const hasStoredRailPreference = storedRailPreferences.railCollapsedShowLabels !== null
          || storedRailPreferences.railAutoPopupOnHover !== null
        if (hasStoredRailPreference) {
          setPreferences((current) => {
            const next = {
              ...current,
              railCollapsedShowLabels: storedRailPreferences.railCollapsedShowLabels
                ?? current.railCollapsedShowLabels,
              railAutoPopupOnHover: storedRailPreferences.railAutoPopupOnHover
                ?? current.railAutoPopupOnHover,
            }
            lastSyncedRailPreferences.current = {
              railCollapsedShowLabels: next.railCollapsedShowLabels,
              railAutoPopupOnHover: next.railAutoPopupOnHover,
            }
            return current.railCollapsedShowLabels === next.railCollapsedShowLabels
              && current.railAutoPopupOnHover === next.railAutoPopupOnHover
              ? current
              : next
          })
        } else {
          lastSyncedRailPreferences.current = null
        }
        const storedDigestCadence = isDigestCadence(data?.digestCadence) ? data.digestCadence : null
        const storedQuietHours = data?.quietHours === null
          ? null
          : data?.quietHours === undefined
            ? undefined
            : normalizeQuietHours(data.quietHours)
        const hasStoredDigestPreference = storedDigestCadence !== null || storedQuietHours !== undefined
        if (hasStoredDigestPreference) {
          setPreferences((current) => {
            const nextCadence = storedDigestCadence ?? current.digestCadence
            const nextQuietHours = storedQuietHours === undefined ? current.quietHours : storedQuietHours
            lastSyncedDigestPreferences.current = { digestCadence: nextCadence, quietHours: nextQuietHours }
            if (current.digestCadence === nextCadence && current.quietHours === nextQuietHours) return current
            return { ...current, digestCadence: nextCadence, quietHours: nextQuietHours }
          })
        } else {
          lastSyncedDigestPreferences.current = null
        }
        const storedMemberTheme = data?.[MEMBER_THEME_FIRESTORE_FIELD]
        if (storedMemberTheme === null || isTheme(storedMemberTheme)) {
          const effectiveMemberTheme = (accountLookVersion < 1 && isTheme(storedMemberTheme))
            ? mapToMosaicTheme(storedMemberTheme)
            : storedMemberTheme
          mappedMemberThemeRef.current = effectiveMemberTheme
          setPreferences((current) => {
            lastSyncedMemberTheme.current = effectiveMemberTheme
            return current.memberAppTheme === effectiveMemberTheme
              ? current
              : { ...current, memberAppTheme: effectiveMemberTheme }
          })
        } else {
          mappedMemberThemeRef.current = undefined
          lastSyncedMemberTheme.current = undefined
        }
        const storedSupportLastSeenAt = normalizeIsoString(data?.supportTicketLastSeenAt)
        if (storedSupportLastSeenAt) {
          setPreferences((current) => {
            lastSyncedSupportLastSeenAt.current = storedSupportLastSeenAt
            return current.supportTicketLastSeenAt === storedSupportLastSeenAt
              ? current
              : { ...current, supportTicketLastSeenAt: storedSupportLastSeenAt }
          })
        } else {
          lastSyncedSupportLastSeenAt.current = null
        }
      } catch (error) {
        setAccountLookMove('done')
        console.warn('[ClientPreferences] Could not hydrate account preferences from Firestore.', error)
      }
    })
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const uid = currentUidRef.current
    if (!uid) return
    const nextRailPreferences = {
      railCollapsedShowLabels: preferences.railCollapsedShowLabels,
      railAutoPopupOnHover: preferences.railAutoPopupOnHover,
    }
    const last = lastSyncedRailPreferences.current
    if (last
      && last.railCollapsedShowLabels === nextRailPreferences.railCollapsedShowLabels
      && last.railAutoPopupOnHover === nextRailPreferences.railAutoPopupOnHover) return
    lastSyncedRailPreferences.current = nextRailPreferences
    void setDoc(
      doc(firestoreDb, 'users', uid),
      nextRailPreferences,
      { merge: true },
    ).catch((error) => {
      console.warn('[ClientPreferences] Could not persist rail preferences.', error)
    })
  }, [preferences.railAutoPopupOnHover, preferences.railCollapsedShowLabels])

  useEffect(() => {
    const uid = currentUidRef.current
    if (!uid) return
    const nextDigestPreferences = {
      digestCadence: preferences.digestCadence,
      quietHours: preferences.quietHours,
    }
    const last = lastSyncedDigestPreferences.current
    if (last
      && last.digestCadence === nextDigestPreferences.digestCadence
      && last.quietHours === nextDigestPreferences.quietHours) return
    lastSyncedDigestPreferences.current = nextDigestPreferences
    void setDoc(
      doc(firestoreDb, 'users', uid),
      nextDigestPreferences,
      { merge: true },
    ).catch((error) => {
      console.warn('[ClientPreferences] Could not persist digest preferences.', error)
    })
  }, [preferences.digestCadence, preferences.quietHours])

  useEffect(() => {
    const uid = currentUidRef.current
    if (!uid) return
    if (lastSyncedMemberTheme.current === preferences.memberAppTheme) return
    lastSyncedMemberTheme.current = preferences.memberAppTheme
    void setDoc(
      doc(firestoreDb, 'users', uid),
      { [MEMBER_THEME_FIRESTORE_FIELD]: preferences.memberAppTheme },
      { merge: true },
    ).catch((error) => {
      console.warn('[ClientPreferences] Could not persist member app theme.', error)
    })
  }, [preferences.memberAppTheme])

  useEffect(() => {
    const uid = currentUidRef.current
    if (!uid) return
    if (lastSyncedSupportLastSeenAt.current === preferences.supportTicketLastSeenAt) return
    lastSyncedSupportLastSeenAt.current = preferences.supportTicketLastSeenAt
    void setDoc(
      doc(firestoreDb, 'users', uid),
      { supportTicketLastSeenAt: preferences.supportTicketLastSeenAt },
      { merge: true },
    ).catch((error) => {
      console.warn('[ClientPreferences] Could not persist support ticket seen state.', error)
    })
  }, [preferences.supportTicketLastSeenAt])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches)

    syncViewport()
    mediaQuery.addEventListener('change', syncViewport)
    window.addEventListener('resize', syncViewport)

    return () => {
      mediaQuery.removeEventListener('change', syncViewport)
      window.removeEventListener('resize', syncViewport)
    }
  }, [])

  const updatePreferences = useCallback((patch: Partial<ClientUserPreferences>) => {
    setPreferences((current) => normalizePreferences({ ...current, ...patch }))
  }, [])

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_CLIENT_USER_PREFERENCES)
  }, [])

  const completeAccountLookMove = useCallback(async () => {
    const uid = currentUidRef.current
    setAccountLookMove('done')
    if (!uid) return
    const memberThemeToWrite = mappedMemberThemeRef.current
    const shouldWriteMemberTheme = memberThemeToWrite !== undefined
    const payload: { lookVersion: number; [MEMBER_THEME_FIRESTORE_FIELD]?: AppTheme | null } = {
      lookVersion: 1,
    }
    if (shouldWriteMemberTheme) {
      payload[MEMBER_THEME_FIRESTORE_FIELD] = memberThemeToWrite
    }
    try {
      if (shouldWriteMemberTheme) {
        lastSyncedMemberTheme.current = memberThemeToWrite
      }
      await setDoc(
        doc(firestoreDb, 'users', uid),
        payload,
        { merge: true },
      )
    } catch (error) {
      console.warn('[ClientPreferences] Could not complete account look move.', error)
    }
  }, [])

  const value = useMemo<ClientPreferencesContextValue>(() => ({
    preferences,
    isMobileViewport,
    updatePreferences,
    resetPreferences,
    accountLookMove,
    completeAccountLookMove,
    setFunPackThemeFallback,
  }), [accountLookMove, completeAccountLookMove, isMobileViewport, preferences, resetPreferences, setFunPackThemeFallback, updatePreferences])

  return <ClientPreferencesContext.Provider value={value}>{children}</ClientPreferencesContext.Provider>
}
